/**
 * useBackgroundJobs Hook
 *
 * Fetches background jobs for monitoring.
 */

import { useQuery } from '@tanstack/react-query';
import { backgroundJobService } from '../../../api/background-job.service';
import type { BackgroundJobListQuery, BackgroundJobListResponse, JobStatus } from '../../../types/background-job.types';
import { getErrorMessage } from '../../../lib/utils/errors';

/**
 * Query key factory for background jobs
 */
export const backgroundJobKeys = {
  all: ['background-jobs'] as const,
  lists: () => [...backgroundJobKeys.all, 'list'] as const,
  list: (tenantId: string, applicationId: string, query?: Omit<BackgroundJobListQuery, 'applicationId'>) =>
    [...backgroundJobKeys.lists(), tenantId, applicationId, query] as const,
  details: () => [...backgroundJobKeys.all, 'detail'] as const,
  detail: (tenantId: string, jobId: string) => [...backgroundJobKeys.details(), tenantId, jobId] as const,
};

/**
 * Hook to fetch background jobs for a specific application
 */
export function useBackgroundJobs(
  tenantId: string,
  applicationId: string,
  options?: {
    status?: JobStatus;
    limit?: number;
    cursor?: string;
    enabled?: boolean;
    refetchInterval?: number | false | ((data: BackgroundJobListResponse | undefined) => number | false);
  }
) {
  const { status, limit, cursor, enabled = true, refetchInterval = false } = options || {};

  return useQuery<BackgroundJobListResponse, Error>({
    queryKey: backgroundJobKeys.list(tenantId, applicationId, { status, limit, cursor }),
    queryFn: async () => {
      try {
        return await backgroundJobService.listJobsByApplication(tenantId, applicationId, {
          status,
          limit,
          cursor,
        });
      } catch (error) {
        throw new Error(getErrorMessage(error));
      }
    },
    enabled: enabled && !!tenantId && !!applicationId,
    refetchInterval,
  });
}
